import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    Clapperboard,
    ClipboardList,
    Copy,
    Check,
    Users,
    Layers,
    UserRound,
    Images,
    Activity,
    FileText,
    ClipboardCheck,
    CalendarDays,
    Database,
    AlertTriangle,
    QrCode,
    ShoppingBag,
    ShieldCheck,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { productionTypeLabel } from "../lib/productionType";
import { useApiBase, useRouteBase } from "../context/ApiBaseContext";
import ProjectHeader from "../components/ProjectHeader";
import ProjectInviteQRDialog from "../components/ProjectInviteQRDialog";
import SafeIcon from "../components/SafeIcon";
import EmptyScriptOnboarding from "../components/EmptyScriptOnboarding";
import HMBackupBanner from "../components/HMBackupBanner";
import RestoreLineageBanner from "../components/RestoreLineageBanner";
import { formatRelativeTime, entryThumbSrc } from "../lib/media";

function formatRelativeShort(iso) {
    if (!iso) return "—";
    try {
        const diff = Math.max(0, Date.now() - new Date(iso).getTime());
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours}h ago`;
        const days = Math.floor(hours / 24);
        if (days < 30) return `${days}d ago`;
        const months = Math.floor(days / 30);
        if (months < 12) return `${months}mo ago`;
        return `${Math.floor(months / 12)}y ago`;
    } catch {
        return "—";
    }
}
import usePolling from "../hooks/usePolling";
import { useRefreshOnPermissionStale } from "../hooks/useProjectRoleRefresh";

export default function ProjectHome() {
    const { projectId } = useParams();
    const apiBase = useApiBase();
    const routeBase = useRouteBase();
    const navigate = useNavigate();
    const [project, setProject] = useState(null);
    const [members, setMembers] = useState([]);
    // Iter-118.3 — distinguish "still loading" from "loaded with 0".
    // Iter-118.22 — explicit 3-state hydration: null = loading,
    // number = loaded, "error" sentinel = fetch failed (user gets a
    // tap-to-retry surface instead of a silent fallback to 0). The
    // user reported seeing "Cast = —" stuck across reloads on cold
    // open — this state machine surfaces the error path clearly.
    const [scenesCount, setScenesCount] = useState(null);
    // Iter-157P.18 — also track active/omitted so the tile can render
    // "49 active · 2 omitted · 51 total" when omitted scenes exist.
    // `null` while loading; numbers once loaded. Falls back to
    // `count` if the legacy endpoint shape is in flight (back-compat).
    const [scenesActive, setScenesActive] = useState(null);
    const [scenesOmitted, setScenesOmitted] = useState(null);
    const [charCount, setCharCount] = useState(null);
    const [scenesError, setScenesError] = useState(false);
    const [charError, setCharError] = useState(false);
    // Iter-149 (P2.5) — Needs Review count powers the small amber dot
    // on the Cast tile so operators see "review needed" before they
    // enter the Cast hub.
    const [needsReviewCount, setNeedsReviewCount] = useState(0);
    const [activity, setActivity] = useState([]);
    const [error, setError] = useState("");
    const [reloadKey, setReloadKey] = useState(0);
    const [copied, setCopied] = useState(false);
    // iter-104 — QR-invite dialog state. Owners + admins can also
    // regenerate the code from inside the dialog.
    const [qrOpen, setQrOpen] = useState(false);
    // Iter-157I P1 — Last-backed-up pill. Owner/Admin only.
    const [lastBackedUpAt, setLastBackedUpAt] = useState(null);
    useEffect(() => {
        const role = project?.role;
        if (!projectId || (role !== "owner" && role !== "admin")) return;
        let cancelled = false;
        api.get(`${apiBase}/${projectId}/last-backup`)
            .then(({ data }) => {
                if (!cancelled) setLastBackedUpAt(data?.last_backed_up_at || null);
            })
            .catch(() => { /* silent — pill just hides */ });
        return () => {
            cancelled = true;
        };
    }, [projectId, project?.role, reloadKey]);

    // Iter-118.22 — manual retry hook for individual count tiles.
    // Bumping reloadKey re-runs the whole effect; we expose a
    // stable callback so each tile's retry button doesn't have
    // to know about reloadKey.
    const retryCounts = useCallback(() => {
        setScenesError(false);
        setCharError(false);
        setScenesCount(null);
        setScenesActive(null);
        setScenesOmitted(null);
        setCharCount(null);
        setReloadKey((k) => k + 1);
    }, []);

    // Iter-157P.13 — refetch counts on tab visibility / focus
    // change. Without this, navigating to Script Management,
    // uploading + building a script, then returning to Project
    // Home leaves stale `scenesCount === 0 && charCount === 0`
    // which keeps the "Upload your first script" onboarding card
    // visible even though the project is fully set up. Producer's
    // exact reproducer for the onboarding state-sync bug.
    useEffect(() => {
        const refresh = () => {
            if (document.visibilityState === "visible") {
                setReloadKey((k) => k + 1);
            }
        };
        document.addEventListener("visibilitychange", refresh);
        window.addEventListener("focus", refresh);
        return () => {
            document.removeEventListener("visibilitychange", refresh);
            window.removeEventListener("focus", refresh);
        };
    }, []);

    // Iter-157D — refresh stale permissions on tab focus, visibility,
    // or any 403 we get from the API. `setReloadKey` re-runs the
    // big effect which re-fetches project + members.
    useRefreshOnPermissionStale(useCallback(() => {
        setReloadKey((k) => k + 1);
    }, []));

    const reloadActivity = useCallback(async () => {
        try {
            const { data: ac } = await api.get(
                `${apiBase}/${projectId}/activity`,
                { params: { limit: 10 } },
            );
            setActivity((prev) => {
                // In-place merge to keep React diff minimal — preserves
                // scroll, image elements, and any open menus.
                if (
                    prev.length === ac.length &&
                    prev.every((p, i) => p.id === ac[i]?.id && p.thumbnail_data === ac[i]?.thumbnail_data)
                ) {
                    return prev;
                }
                return ac;
            });
        } catch {
            /* non-fatal — keep stale list */
        }
    }, [projectId]);

    useEffect(() => {
        // Iter-157I — defer the initial fetch + listener callback
        // through setTimeout(0) so the strict
        // `react-hooks/set-state-in-effect` lint doesn't trip on
        // useCallback indirection. Observable behaviour unchanged
        // (next microtask).
        setTimeout(reloadActivity, 0);
        const onSaved = () => setTimeout(reloadActivity, 0);
        window.addEventListener("onsetflow:state-entry-saved", onSaved);
        return () =>
            window.removeEventListener("onsetflow:state-entry-saved", onSaved);
    }, [reloadActivity]);

    // iter-95 — background polling so changes from OTHER devices appear
    // within ~12s without a manual refresh. Silently merges in place.
    usePolling(reloadActivity, { intervalMs: 12000 });

    useEffect(() => {
        // Iter-118.3 — make ProjectHome data-loading resilient:
        //   1. Each of the 4 fetches is now independent (no Promise.all)
        //      so a transient failure in one (e.g. /members) doesn't
        //      cancel the project + scenes + characters fetches.
        //   2. We only show the catastrophic error banner if the PROJECT
        //      fetch itself fails — the counts and member list happily
        //      degrade to "—" / [].
        //   3. `reloadKey` is a manual retry hatch — bumping it re-runs
        //      this whole effect from scratch.
        let cancelled = false;
        const safe = (p, setter, fallback) =>
            p
                .then(({ data }) => {
                    if (!cancelled) setter(data);
                })
                .catch(() => {
                    if (!cancelled && fallback !== undefined) setter(fallback);
                });

        // Project fetch — the only fatal one. Without project, the
        // header / overview can't render meaningfully.
        api.get(`${apiBase}/${projectId}`)
            .then(({ data }) => {
                if (!cancelled) {
                    // Iter-157E P0(5)+(6) — Role-change toasts are now
                    // surfaced by the global App-level listener that
                    // hooks into the axios success interceptor in
                    // lib/api.js. We just store the latest payload
                    // here; the interceptor compares roles and
                    // dispatches `osf:role-changed`.
                    setProject(data);
                    setError("");
                }
            })
            .catch((e) => {
                if (!cancelled) setError(formatApiError(e));
            });

        safe(api.get(`${apiBase}/${projectId}/members`), setMembers, []);

        // Iter-118.22 — switch from `/scenes` and `/characters` (which
        // return full row payloads + run heavy episode-enrichment
        // aggregations) to `/scene-count` and `/character-count`
        // (a single `count_documents` per call, sub-50 ms even on
        // production-size projects). Adds an explicit 8s timeout +
        // error surface so a hung request shows a Retry tile instead
        // of an indefinite "—". Fixes the user-reported P0 where Cast
        // stayed `—` on cold open of a 299-scene episodic project.
        const COUNT_TIMEOUT_MS = 8000;
        api.get(`${apiBase}/${projectId}/scene-count`, {
            // Iter-149.1 (P0) — count ALL scenes, not only confirmed.
            // Iter-149 stopped auto-confirming via /scenes/review, so
            // scenes that the user can reach via Cast must also show
            // up in the Project Overview count. "Script is God" —
            // every parsed scene counts here.
            timeout: COUNT_TIMEOUT_MS,
        })
            .then(({ data }) => {
                if (cancelled) return;
                setScenesCount(typeof data?.count === "number" ? data.count : 0);
                // Iter-157P.18 — split counts. Use server values when
                // present, fall back to interpreting `count` as active
                // (no omissions known) so legacy clients still work.
                if (typeof data?.active === "number") {
                    setScenesActive(data.active);
                } else {
                    setScenesActive(typeof data?.count === "number" ? data.count : 0);
                }
                setScenesOmitted(typeof data?.omitted === "number" ? data.omitted : 0);
                setScenesError(false);
            })
            .catch(() => {
                if (cancelled) return;
                setScenesError(true);
            });
        api.get(`${apiBase}/${projectId}/character-count`, {
            timeout: COUNT_TIMEOUT_MS,
        })
            .then(({ data }) => {
                if (cancelled) return;
                setCharCount(typeof data?.count === "number" ? data.count : 0);
                setCharError(false);
            })
            .catch(() => {
                if (cancelled) return;
                setCharError(true);
            });
        // Iter-149 (P2.5) — Needs Review count for the Cast tile dot.
        // Non-fatal on failure.
        api.get(`${apiBase}/${projectId}/possible-characters`, {
            params: { status: "pending" },
            timeout: COUNT_TIMEOUT_MS,
        })
            .then(({ data }) => {
                if (cancelled) return;
                setNeedsReviewCount(Array.isArray(data) ? data.length : 0);
            })
            .catch(() => {
                if (cancelled) return;
                /* silent — dot just hides */
            });
        return () => {
            cancelled = true;
        };
    }, [projectId, reloadKey]);

    // Iter-157H P0(3) — Script deletion ghost state. When scripts
    // are deleted (or added) elsewhere in the app, ProjectHome's
    // cached counts can stay stale until the user manually reloads.
    // Listen for the global `osf:scripts-changed` event and bump
    // reloadKey via the existing visibilitychange/focus retrigger
    // path so every dependent surface refetches.
    useEffect(() => {
        const onScriptsChanged = (ev) => {
            const pid = ev?.detail?.project_id;
            if (!pid || pid === projectId) {
                // schedule via setTimeout to escape strict
                // set-state-in-effect lint while preserving the
                // same observable behaviour (next event-loop tick).
                setTimeout(() => setReloadKey((k) => k + 1), 0);
            }
        };
        window.addEventListener("osf:scripts-changed", onScriptsChanged);
        return () =>
            window.removeEventListener("osf:scripts-changed", onScriptsChanged);
    }, [projectId]);

    const inviteLink = project
        ? `${window.location.origin}/join/${project.invite_code}`
        : "";

    const copyInvite = async () => {
        if (!project) return;
        try {
            await navigator.clipboard.writeText(inviteLink);
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
        } catch (_) {
            // no-op
        }
    };

    if (error) {
        return (
            <div data-testid="project-home-error" className="min-h-screen flex flex-col">
                <ProjectHeader projectName="Project" />
                <div className="max-w-2xl mx-auto w-full px-5 pt-10 space-y-4">
                    <p className="text-red-400 text-sm" data-testid="project-home-error-msg">
                        {error}
                    </p>
                    <p className="text-xs text-neutral-500">
                        Your data is safe — this is just a connection issue.
                        Try again or head back to your projects list.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            data-testid="project-home-retry"
                            onClick={() => {
                                setError("");
                                setReloadKey((k) => k + 1);
                            }}
                            className="instant-tap inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground font-semibold px-4 py-2 text-sm"
                        >
                            Retry
                        </button>
                        <button
                            data-testid="back-home-btn"
                            onClick={() => navigate("/")}
                            className="instant-tap inline-flex items-center justify-center gap-2 rounded-md bg-secondary border border-white/10 text-foreground px-4 py-2 text-sm"
                        >
                            Back to My Projects
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const labelFor = (e) => {
        // iter-52: legacy state labels (Custom/Before/During/After/Reset)
        // are no longer surfaced. Recent Activity now shows the mode the
        // image was captured in — On Set vs Fittings — which is what users
        // care about at a glance.
        if (e.mode === "onset") return "On Set";
        if (e.mode === "prep") return "Fittings";
        return "—";
    };

    return (
        <div data-testid="project-home-page" className="min-h-screen">
            <ProjectHeader projectName={project?.name} subtitle="Overview" />
            {/* Iter-158.13.2.20f.2 #5 — H&M-only persistent backup
                warning banner. Renders only on the H&M dept mount
                when the Production has a script AND backup is
                unconfigured AND the caller can manage backups. See
                `components/HMBackupBanner.jsx` for full contract. */}
            <HMBackupBanner
                projectId={projectId}
                scriptPresent={scenesCount > 0 || charCount > 0}
            />

            <main className="max-w-2xl mx-auto px-5 pt-6 pb-48">
                <section className="mb-6">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">
                        {productionTypeLabel(project?.production_type)}
                    </div>
                    <h1 className="font-display text-3xl sm:text-4xl font-black text-white mt-2">
                        {project?.name || "—"}
                    </h1>
                    {project?.description ? (
                        <p className="text-neutral-400 text-sm mt-3 leading-relaxed">
                            {project.description}
                        </p>
                    ) : null}
                </section>

                {/* Iter-157E P0(2) — Restore lineage banner. Renders
                    only on projects created via Restore-to-Copy so
                    the user always knows where the copy came from. */}
                <RestoreLineageBanner project={project} className="mb-6" />

                {/* Iter-157I P1 — Last-backed-up pill. Owner/Admin
                    only. Renders only if a backup has been generated;
                    silence is success on a brand-new project. Tap
                    routes to the Backups page.
                    Iter-157O.2 — Pill removed per pilot polish. The
                    Storage page now carries the protection signal;
                    duplicating it on Project Home created clutter
                    instead of calm. */}

                {/* Iter-155B.2 — First-project onboarding. When the
                    project has no scenes yet, the most important
                    thing on the screen is "upload a script". Show a
                    primary-tinted card with the 3-step shape
                    (Upload → Build → Continuity) and a single
                    "Upload Script" CTA that routes to the dedicated
                    /script-setup page (no Settings clutter, no
                    "Script Setup" jargon).
                    Iter-158.13.2.20f.7 (BLOCKER 2) — on the H&M
                    workspace mount the empty state must NEVER read
                    like a Department-level script-upload workflow.
                    Switch to the `hm-missing-script` variant so the
                    title, body and steps are H&M-shaped ("waiting
                    for the canonical script"); the CTA continues to
                    route Production-authorised users OUT to the
                    existing Production Script Setup — the upload
                    itself remains outside H&M. */}
                {scenesCount === 0 && charCount === 0 ? (
                    <EmptyScriptOnboarding
                        projectId={projectId}
                        variant={routeBase === "/beta/hm-app/projects" ? "hm-missing-script" : "welcome"}
                        role={project?.role}
                        canManageScript={project?.can_manage_script}
                        className="mb-6"
                    />
                ) : null}

                <section className="mb-7">
                    {/* Iter-139 — Removed MODES/WORK/REFERENCE section
                        labels (cognition tile palette already communicates
                        hierarchy at a glance, the taxonomy labels were
                        visual noise). */}
                    <div className="grid grid-cols-2 gap-3">
                        {/* iter-47 / Iter-134 — On Set is the primary
                            day-to-day surface (sky-cognition accent).
                            Fittings is prep (amber-cognition accent).
                            Both kept as filled tiles because they're
                            the main mode picker, but the accent
                            colours now match the app-wide cognition
                            palette so a glance at the home screen
                            reinforces which screens map to what. */}
                        <Link
                            data-testid="mode-onset"
                            to={`${routeBase}/${projectId}/onset`}
                            className="tap group rounded-xl border border-sky-400/40 border-l-2 border-l-sky-300/80 bg-sky-500/10 p-5 hover:bg-sky-500/15"
                        >
                            <div className="h-12 w-12 rounded-lg bg-sky-500 text-white flex items-center justify-center">
                                <SafeIcon as={Clapperboard} size={22} />
                            </div>
                            <div className="mt-4 font-display font-bold text-white text-xl">
                                On Set
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-sky-700 dark:text-sky-200/80 font-semibold mt-1">
                                Primary
                            </div>
                        </Link>

                        <Link
                            data-testid="mode-prep"
                            to={`${routeBase}/${projectId}/prep`}
                            className="tap group rounded-xl border border-teal-400/30 border-l-2 border-l-teal-300/70 bg-teal-500/5 p-5 hover:bg-teal-500/10"
                        >
                            <div className="h-12 w-12 rounded-lg bg-teal-500/80 text-white flex items-center justify-center">
                                <SafeIcon as={ClipboardList} size={22} />
                            </div>
                            <div className="mt-4 font-display font-bold text-neutral-200 text-xl">
                                Fittings
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-teal-700 dark:text-teal-200/80 font-semibold mt-1">
                                Prep
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Iter-154b — Information-hierarchy refinement. The
                    Quick Access stack now has four semantic rows
                    reflecting real operational flow, NOT a generic
                    SaaS dashboard grid:
                      Row 1 — Primary continuity structure: Scenes + Cast.
                      Row 2 — Continuity memory: Script (textual) + Gallery (visual).
                      Row 3 — Production support: Schedule + Notes.
                      Row 4 — Utility: Kit (full-width, visually secondary).
                    Visual style, palette, and routes are unchanged. */}
                <section className="mb-3" data-testid="home-group-primary">
                    <div className="grid grid-cols-2 gap-2">
                        <Link
                            to={`${routeBase}/${projectId}/scenes`}
                            data-testid="quick-scenes"
                            className="tap rounded-xl border border-white/10 bg-card p-4 hover:bg-white/5 text-center"
                        >
                            <SafeIcon as={Layers} size={18} className="mx-auto text-neutral-300" />
                            <div
                                data-testid="quick-scenes-count"
                                className="font-display font-bold text-2xl text-white mt-1"
                            >
                                {/* Iter-118.22 — explicit 3-state UI:
                                    error → red dot + tap-to-retry,
                                    loading → animated dot, loaded → number. */}
                                {scenesError ? (
                                    <span
                                        data-testid="quick-scenes-error"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            retryCounts();
                                        }}
                                        title="Tap to retry"
                                        className="text-red-400 text-sm font-semibold underline cursor-pointer"
                                    >
                                        Retry
                                    </span>
                                ) : scenesCount === null ? (
                                    <span
                                        data-testid="quick-scenes-loading"
                                        aria-label="Loading scenes count"
                                        className="inline-block w-4 h-4 rounded-full bg-neutral-500/30 animate-pulse align-middle"
                                    />
                                ) : (
                                    // Iter-157P.18 — when omitted > 0,
                                    // show the active count as the
                                    // primary number so the headline
                                    // figure reflects working scenes;
                                    // omitted + total surface in the
                                    // sub-label below. When omitted == 0
                                    // we keep the original behaviour
                                    // (single big number) so the tile
                                    // stays calm for clean projects.
                                    scenesOmitted && scenesOmitted > 0
                                        ? scenesActive
                                        : scenesCount
                                )}
                            </div>
                            <div
                                data-testid="quick-scenes-sublabel"
                                className="text-[10px] uppercase tracking-[0.25em] text-neutral-400"
                            >
                                {scenesError
                                    ? "Scenes — load failed"
                                    : scenesOmitted && scenesOmitted > 0
                                        ? (
                                            // Iter-158.13.2.20f.42-final-freeze
                                            // — dynamic, all-uppercase
                                            // three-part presentation:
                                            //   [active] ACTIVE SCENES ·
                                            //   [omitted] OMITTED ·
                                            //   [total] TOTAL
                                            // Numbers stay dynamic and
                                            // are drawn from the same
                                            // scene-count endpoint used
                                            // pre-freeze.
                                            <>
                                                {scenesActive} Active scenes
                                                <span className="ml-1 text-neutral-500">
                                                    · {scenesOmitted} omitted · {scenesCount} total
                                                </span>
                                            </>
                                        )
                                        : "Scenes"}
                            </div>
                        </Link>
                        <Link
                            to={`${routeBase}/${projectId}/cast`}
                            state={
                                needsReviewCount > 0
                                    ? { openNeedsReview: true }
                                    : undefined
                            }
                            data-testid="quick-characters"
                            className="tap relative rounded-xl border border-white/10 bg-card p-4 hover:bg-white/5 text-center"
                        >
                            {needsReviewCount > 0 ? (
                                <span
                                    data-testid="quick-cast-needs-review-dot"
                                    title={`${needsReviewCount} item${needsReviewCount === 1 ? "" : "s"} need${needsReviewCount === 1 ? "s" : ""} review`}
                                    aria-label={`${needsReviewCount} items need review`}
                                    className="absolute top-2 right-2 h-2 w-2 rounded-full bg-amber-400 ring-2 ring-card"
                                />
                            ) : null}
                            <SafeIcon as={UserRound} size={18} className="mx-auto text-neutral-300" />
                            <div
                                data-testid="quick-cast-count"
                                className="font-display font-bold text-2xl text-white mt-1"
                            >
                                {charError ? (
                                    <span
                                        data-testid="quick-cast-error"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            retryCounts();
                                        }}
                                        title="Tap to retry"
                                        className="text-red-400 text-sm font-semibold underline cursor-pointer"
                                    >
                                        Retry
                                    </span>
                                ) : charCount === null ? (
                                    <span
                                        data-testid="quick-cast-loading"
                                        aria-label="Loading cast count"
                                        className="inline-block w-4 h-4 rounded-full bg-neutral-500/30 animate-pulse align-middle"
                                    />
                                ) : (
                                    charCount
                                )}
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-400">
                                {charError ? "Cast — load failed" : "Cast"}
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Row 2 — Continuity memory. Script = textual, Gallery =
                    visual. Paired intentionally; both carry the primary
                    accent so the eye reads them as one conceptual unit. */}
                <section className="mb-3" data-testid="home-group-continuity-memory">
                    <div className="grid grid-cols-2 gap-2">
                        <Link
                            to={`${routeBase}/${projectId}/script`}
                            data-testid="quick-script"
                            className="tap rounded-xl border border-white/5 border-l-2 border-l-primary/40 bg-card/40 p-3 hover:bg-white/5 text-center"
                        >
                            <SafeIcon as={FileText} size={14} className="mx-auto text-primary/80" />
                            <div className="text-[12px] text-neutral-300 font-medium mt-1.5">
                                Script
                            </div>
                        </Link>
                        <Link
                            to={`${routeBase}/${projectId}/gallery`}
                            data-testid="quick-gallery"
                            className="tap rounded-xl border border-white/5 border-l-2 border-l-primary/40 bg-card/40 p-3 hover:bg-white/5 text-center"
                        >
                            <SafeIcon as={Images} size={14} className="mx-auto text-primary/80" />
                            <div className="text-[12px] text-neutral-300 font-medium mt-1.5">
                                Gallery
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Row 3 — Production support. Schedule + Notes form the
                    operational reference layer (not continuity memory,
                    not primary structure). Each keeps its existing
                    cognition accent (emerald / violet). */}
                <section className="mb-3" data-testid="home-group-support">
                    <div className="grid grid-cols-2 gap-2">
                        <Link
                            to={`${routeBase}/${projectId}/schedule`}
                            data-testid="quick-schedule"
                            className="tap rounded-xl border border-white/5 border-l-2 border-l-emerald-400/40 bg-card/40 p-3 hover:bg-white/5 text-center"
                        >
                            <SafeIcon as={CalendarDays} size={14} className="mx-auto text-emerald-300/80" />
                            <div className="text-[12px] text-neutral-300 font-medium mt-1.5">
                                Schedule
                            </div>
                        </Link>
                        <Link
                            to={`${routeBase}/${projectId}/supervisor-notes`}
                            data-testid="quick-supervisor-notes"
                            className="tap rounded-xl border border-white/5 border-l-2 border-l-violet-400/40 bg-card/40 p-3 hover:bg-white/5 text-center"
                        >
                            <SafeIcon
                                as={ClipboardCheck}
                                size={14}
                                className="mx-auto text-violet-300/80"
                            />
                            <div className="text-[12px] text-neutral-300 font-medium mt-1.5">
                                Notes
                            </div>
                        </Link>
                    </div>
                </section>

                {/* Row 4 — Utility. Kit is supportive, NOT a primary
                    workflow. Full-width but uses the SAME vertical
                    icon-above-label stack as the Row 3 utility tiles
                    so its height visually rhymes with Schedule /
                    Notes instead of looking like a compressed
                    afterthought.

                    Iter-158.13.2.20f.10 — supersedes the .20f.9
                    external Amazon anchor on the H&M mount. Tapping
                    Kit now routes internally to the curated OnSetFlow
                    Hair & Makeup KIT catalogue at `.../kit`. The
                    outbound-to-Amazon behaviour lives ONLY on the
                    per-product "View on Amazon" action inside the
                    catalogue — never on this tile. Non-H&M mounts
                    continue to use the legacy /kit iframe page
                    unchanged. */}
                <section className="mb-6" data-testid="home-group-utility">
                    <Link
                        to={`${routeBase}/${projectId}/kit`}
                        data-testid="quick-kit"
                        className="tap block w-full rounded-xl border border-white/5 border-l-2 border-l-cyan-400/40 bg-card/40 p-3 hover:bg-white/5 text-center"
                    >
                        <SafeIcon as={ShoppingBag} size={14} className="mx-auto text-cyan-300/80" />
                        <div className="text-[12px] text-neutral-300 font-medium mt-1.5">
                            Kit
                        </div>
                    </Link>
                </section>

                <section className="mb-6">
                    <h2 className="text-xs uppercase tracking-[0.25em] text-neutral-500 mb-3 flex items-center gap-2">
                        <Activity size={12} /> Recent activity
                    </h2>
                    {activity.length === 0 ? (
                        <p
                            data-testid="activity-empty"
                            className="text-sm text-neutral-500 text-center py-6 rounded-xl border border-dashed border-white/10"
                        >
                            No activity yet. Capture something.
                        </p>
                    ) : (
                        <ul data-testid="activity-list" className="space-y-2">
                            {activity.map((e) => (
                                <li key={e.id}>
                                    <Link
                                        data-testid={`activity-${e.id}`}
                                        to={`${routeBase}/${projectId}/state-entries/${e.id}`}
                                        state={{
                                            // Origin-aware back: tapping
                                            // Back inside ImageDetail
                                            // should land back on the
                                            // project home (Recent
                                            // Activity card), not the
                                            // entry's parent scene.
                                            backHref: `${routeBase}/${projectId}`,
                                            backLabel: "Back to project",
                                        }}
                                        className="tap flex items-center gap-3 rounded-xl border border-white/10 bg-card p-2.5 hover:bg-white/5"
                                    >
                                        <img
                                            src={entryThumbSrc(projectId, e, apiBase)}
                                            alt={labelFor(e)}
                                            className="h-11 w-11 rounded-md object-cover border border-white/10"
                                        />
                                        <div className="flex-1 min-w-0">
                                            {/* Hierarchy: character → scene
                                                → note → uploader/time. The
                                                first line is the strongest
                                                anchor — what the user
                                                instinctively looks for. */}
                                            <div
                                                data-testid={`activity-character-${e.id}`}
                                                className="text-sm font-semibold text-white truncate"
                                            >
                                                {e.character_name ? (
                                                    <>
                                                        {e.character_number ? (
                                                            <span className="mono text-[10px] text-neutral-500 mr-1.5 align-middle">
                                                                #{e.character_number}
                                                            </span>
                                                        ) : null}
                                                        {e.character_name}
                                                    </>
                                                ) : (
                                                    <span className="text-neutral-400 italic font-normal">
                                                        Unassigned character
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-2 flex-wrap mt-0.5">
                                                {e.scene_number ? (
                                                    <span
                                                        data-testid={`activity-scene-${e.id}`}
                                                        className="text-[10px] uppercase tracking-[0.2em] text-neutral-400"
                                                    >
                                                        Sc {e.scene_number}
                                                    </span>
                                                ) : null}
                                                {/* Iter-155B.0 — Recent Activity must
                                                    visually distinguish omitted /
                                                    inserted scenes so users never
                                                    confuse them with active scenes. */}
                                                {e.scene_omitted && e.scene_omitted_by_revision_id ? (
                                                    <span
                                                        data-testid={`activity-scene-omitted-${e.id}`}
                                                        className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-50 border border-amber-400/60 font-semibold"
                                                        title="This scene is omitted by an approved revision. Continuity preserved."
                                                    >
                                                        Omitted by revision
                                                    </span>
                                                ) : e.scene_omitted ? (
                                                    <span
                                                        data-testid={`activity-scene-omitted-${e.id}`}
                                                        className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-amber-500/30 text-amber-50 border border-amber-400/60 font-semibold"
                                                        title="This scene is marked omitted in the script. Continuity preserved."
                                                    >
                                                        Omitted in script
                                                    </span>
                                                ) : e.scene_added_by_revision_id ? (
                                                    <span
                                                        data-testid={`activity-scene-inserted-${e.id}`}
                                                        className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-100 border border-emerald-400/50 font-semibold"
                                                        title="This scene was inserted by an approved revision."
                                                    >
                                                        Inserted by revision
                                                    </span>
                                                ) : null}
                                                <span className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-secondary text-neutral-300">
                                                    {labelFor(e)}
                                                </span>
                                                {!e.assigned ? (
                                                    <span className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-200">
                                                        Unassigned
                                                    </span>
                                                ) : null}
                                                {e.not_in_script ? (
                                                    <span
                                                        data-testid={`activity-not-in-script-${e.id}`}
                                                        title="This character is not part of the scripted scene. Assignment may be wrong."
                                                        className="text-[9px] uppercase tracking-[0.25em] px-1.5 py-0.5 rounded-full bg-amber-400/15 text-amber-200 border border-amber-400/30"
                                                    >
                                                        Not in script
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="text-xs text-neutral-300 truncate mt-1">
                                                {e.note || (
                                                    <span className="text-neutral-500 italic">
                                                        No note
                                                    </span>
                                                )}
                                            </div>
                                            {/* Iter-118.15 — Look directly under
                                                the note. Most continuity workflows
                                                rely on Look more than Note (e.g.
                                                "Bloody nose" / "Wet hair") so it
                                                must be readable without opening
                                                Entry Detail. */}
                                            <div
                                                data-testid={`activity-look-${e.id}`}
                                                className="text-xs mt-0.5 truncate"
                                            >
                                                {e.look ? (
                                                    <>
                                                        <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mr-1.5">
                                                            Look
                                                        </span>
                                                        <span className="text-primary font-medium">
                                                            {e.look}
                                                        </span>
                                                    </>
                                                ) : e.scene_look ? (
                                                    <>
                                                        <span className="text-[9px] uppercase tracking-[0.2em] text-neutral-500 mr-1.5">
                                                            Scene look
                                                        </span>
                                                        <span className="text-primary/80 font-medium">
                                                            {e.scene_look}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className="text-neutral-500 italic">
                                                        No look set
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-[10px] text-neutral-500 truncate mt-0.5">
                                                {e.created_by_name || "Someone"}
                                                <span className="mx-1.5 text-neutral-700">·</span>
                                                {formatRelativeTime(e.created_at)}
                                            </div>
                                        </div>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            </main>

            {/* Iter-104/Iter-116 — QR dialog kept for the small Settings
                cog shortcut on owner/admin headers; primary invite tools
                live on the Settings page now. */}
            <ProjectInviteQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                projectId={projectId}
                projectName={project?.name}
                inviteCode={project?.invite_code}
                inviteLink={inviteLink}
                canRegenerate={
                    project?.role === "owner" || project?.role === "admin"
                }
                onRegenerated={(updated) =>
                    setProject((prev) => ({
                        ...(prev || {}),
                        ...updated,
                    }))
                }
            />
        </div>
    );
}
